import React,{useState,useCallback} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {openWhatsApp} from '../lib/whatsapp';

const {FiArrowLeft,FiArrowRight,FiArrowUpRight,FiX,FiPlus}=FiIcons;

const galleryImages=[
  {src: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=1400&q=85',alt: 'Textural loft with linen furnishings',tag: 'Living',size: 'xl'},
  {src: 'https://images.unsplash.com/photo-1567016432779-094069958ea5?auto=format&fit=crop&w=1000&q=85',alt: 'Vintage styled living corner',tag: 'Living',size: 'sm'},
  {src: 'https://images.unsplash.com/photo-1505693416388-ac5ce068fe8e?auto=format&fit=crop&w=1000&q=85',alt: 'Reading nook with layered textures',tag: 'Nook',size: 'sm'},
  {src: 'https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&w=1000&q=85',alt: 'Bathroom with stone and brass',tag: 'Bath',size: 'sm'},
  {src: 'https://images.unsplash.com/photo-1618220179428-22a90a7ddea1?auto=format&fit=crop&w=1000&q=85',alt: 'Home office with warm palette',tag: 'Office',size: 'sm'},
  {src: 'https://images.unsplash.com/photo-1615873968403-89e062623118?auto=format&fit=crop&w=1400&q=85',alt: 'Sculptural dining space',tag: 'Dining',size: 'wide'},
  {src: 'https://images.unsplash.com/photo-1631679706909-1844bbd07221?auto=format&fit=crop&w=1400&q=85',alt: 'Modern kitchen with warm wood tones',tag: 'Kitchen',size: 'wide'},
  {src: 'https://images.unsplash.com/photo-1524758631624-e2822e304c36?auto=format&fit=crop&w=1400&q=85',alt: 'Primary suite with plaster walls',tag: 'Suite',size: 'lg'},
  {src: 'https://images.unsplash.com/photo-1616594039964-ae9021a400a0?auto=format&fit=crop&w=1000&q=85',alt: 'Lounge with fireplace and art',tag: 'Lounge',size: 'sm'},
  {src: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1000&q=85',alt: 'Modern villa interior with courtyard view',tag: 'Villa',size: 'sm'},
  {src: 'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?auto=format&fit=crop&w=1000&q=85',alt: 'Quiet layered interior with vintage furniture',tag: 'Studio',size: 'sm'},
  {src: 'https://images.unsplash.com/photo-1600573472556-e636c2acda88?auto=format&fit=crop&w=1000&q=85',alt: 'Minimalist bedroom with warm light',tag: 'Bedroom',size: 'sm'},
  {src: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=1400&q=85',alt: 'Warm modern living space',tag: 'Living',size: 'wide'},
  {src: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1400&q=85',alt: 'Brownstone interior with natural light',tag: 'Living',size: 'wide'}
];

const sizeMap={
  xl: {col: 'span 2',row: 'span 2'},
  lg: {col: 'span 2',row: 'span 2'},
  wide: {col: 'span 2',row: 'span 1'},
  md: {col: 'span 1',row: 'span 1'},
  sm: {col: 'span 1',row: 'span 1'}
};

function GallerySection() {
  const [lightbox,setLightbox]=useState(null);
  const [filter,setFilter]=useState('All');

  const tags=['All',...Array.from(new Set(galleryImages.map((g)=> g.tag)))];
  const visible=filter==='All' ? galleryImages : galleryImages.filter((g)=> g.tag===filter);

  const closeLightbox=useCallback(()=> setLightbox(null),[]);
  const nextImage=useCallback(()=> {
    setLightbox((prev)=> (prev===null ? prev : (prev + 1) % visible.length));
  },[visible.length]);
  const prevImage=useCallback(()=> {
    setLightbox((prev)=> (prev===null ? prev : (prev - 1 + visible.length) % visible.length));
  },[visible.length]);

  return (
    <section className="gallery-section" id="gallery">
      <div className="section-wrap gallery-header">
        <div className="gallery-heading-row">
          <div>
            <p className="eyebrow">The gallery</p>
            <h2> A closer look at <br /><em>the details.</em> </h2>
          </div>
          <p className="section-intro"> Fourteen moments from recent projects — the corners,the textures,and the quiet details that make a house feel like home. </p>
        </div>
        <div className="gallery-filters">
          {tags.map((tag)=> (
            <button key={tag} type="button" className={`gallery-filter ${filter===tag ? 'is-active' : ''}`} onClick={()=> setFilter(tag)}> {tag} </button>
          ))}
        </div>
      </div>
      <div className="gallery-masonry section-wrap">
        {visible.map((img,i)=> (
          <motion.button type="button" key={img.src} className="gallery-masonry-tile" style={{gridColumn: sizeMap[img.size].col,gridRow: sizeMap[img.size].row}} onClick={()=> setLightbox(i)} initial={{opacity: 0,scale: 0.96}} whileInView={{opacity: 1,scale: 1}} viewport={{once: true,amount: 0.1}} transition={{duration: 0.5,delay: (i % 4) * 0.06}} >
            <img src={img.src} alt={img.alt} loading="lazy" />
            <span className="gallery-masonry-overlay">
              <span className="gallery-masonry-plus"><SafeIcon icon={FiPlus} /></span>
              <span className="gallery-masonry-tag">{img.tag}</span>
            </span>
          </motion.button>
        ))}
      </div>
      <div className="gallery-cta-wrap section-wrap">
        <button className="gallery-cta" type="button" onClick={()=> openWhatsApp("Hello Forma Interiors,I'd love to see more of your portfolio.")}> View full portfolio <SafeIcon icon={FiArrowUpRight} /> </button>
      </div>
      <AnimatePresence>
        {lightbox !==null && (
          <motion.div className="lightbox" initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} transition={{duration: 0.3}} onClick={closeLightbox}>
            <button className="lightbox-close" type="button" aria-label="Close" onClick={closeLightbox}> <SafeIcon icon={FiX} /> </button>
            <button className="lightbox-nav lightbox-prev" type="button" aria-label="Previous" onClick={(e)=> {e.stopPropagation();prevImage();}}> <SafeIcon icon={FiArrowLeft} /> </button>
            <motion.div className="lightbox-stage" key={lightbox} initial={{opacity: 0,scale: 0.96}} animate={{opacity: 1,scale: 1}} transition={{duration: 0.3}} onClick={(e)=> e.stopPropagation()}>
              <img src={visible[lightbox].src} alt={visible[lightbox].alt} />
              <div className="lightbox-caption">
                <span className="gallery-tile-tag">{visible[lightbox].tag}</span>
                <span className="lightbox-counter"> {String(lightbox + 1).padStart(2,'0')} / {String(visible.length).padStart(2,'0')} </span>
              </div>
            </motion.div>
            <button className="lightbox-nav lightbox-next" type="button" aria-label="Next" onClick={(e)=> {e.stopPropagation();nextImage();}}> <SafeIcon icon={FiArrowRight} /> </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

export default GallerySection;
